import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { Button } from '../../../../web/components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../../web/components-v2/Dialog';
import { Input } from '../../../../web/components-v2/Input';
import { THEME } from '../../../../web/components-v2/Theme/theme';

export interface IDuplicateTemplateModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	templateName: string;
	onConfirm: (newName: string) => void;
	isLoading?: boolean;
}

/**
 * Modal for duplicating a survey template.
 * Prompts user for new template name with validation.
 */
export function DuplicateTemplateModal({
	open,
	onOpenChange,
	templateName,
	onConfirm,
	isLoading = false,
}: IDuplicateTemplateModalProps) {
	const [newName, setNewName] = React.useState('');

	// Initialize form when modal opens
	React.useEffect(() => {
		if (open) {
			setNewName(`Copy of ${templateName}`);
		} else {
			setNewName('');
		}
	}, [open, templateName]);

	const nameCharsRemaining = 100 - newName.length;
	const isValid = newName.trim().length > 0;

	const handleConfirm = () => {
		if (!isValid || isLoading) {
			return;
		}
		onConfirm(newName.trim());
	};

	const handleClose = () => {
		if (!isLoading) {
			onOpenChange(false);
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && isValid && !isLoading) {
			handleConfirm();
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogPopup styles={[s.dialogContent]}>
				<DialogHeader>
					<DialogTitle>Duplicate Survey Template</DialogTitle>
				</DialogHeader>

				<DialogBody styles={[s.dialogBody]}>
					<div className={css(s.fieldGroup)}>
						<label htmlFor='template-name' className={css(s.label)}>
							New Template Name *
						</label>
						<Input
							id='template-name'
							type='text'
							value={newName}
							onChange={e => setNewName(e.target.value.slice(0, 100))}
							onKeyDown={handleKeyDown}
							placeholder='Enter new template name'
							maxLength={100}
							disabled={isLoading}
							autoFocus
						/>
						<div className={css(s.characterCount)}>
							{nameCharsRemaining} character{nameCharsRemaining !== 1 ? 's' : ''} remaining
						</div>
					</div>
				</DialogBody>

				<DialogFooter>
					<Button variant='primary' onClick={handleConfirm} disabled={!isValid} isLoading={isLoading}>
						Duplicate
					</Button>
					<Button variant='secondary' onClick={handleClose} disabled={isLoading}>
						Cancel
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
}

const s = StyleSheet.create({
	dialogContent: {
		maxWidth: 500,
	},
	dialogBody: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.lg,
	},
	fieldGroup: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.sm,
	},
	label: {
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.main,
	},
	characterCount: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
		marginTop: THEME.spacing.xs,
	},
});
